import React, { useEffect } from 'react';
import { BEACONCHAIN_URL } from '../utils/envVars';
import {
  BeaconChainValidatorResponse,
  BeaconChainValidator,
} from '../pages/TopUp/types';
import { useState } from 'react';
import {
  NetworkChainId,
  AllowedNetworks,
} from '../pages/ConnectWallet/web3Utils';

interface Validators {
  validators: BeaconChainValidator[];
  loading: boolean;
  setLoading: (l: boolean) => void;
  validatorLoadError: boolean;
  showDepositVerificationWarning: boolean;
}

const indexesPaginationSize = 200;
const validatorPaginationSize = 100;

const useValidators = (
  account: string | null | undefined,
  chainId: number | undefined,
  active: boolean
): Validators => {
  const [validators, setValidators] = useState<BeaconChainValidator[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [validatorLoadError, setValidatorLoadError] = React.useState<boolean>(
    false
  );
  const [
    showDepositVerificationWarning,
    setShowDepositVerificationWarning,
  ] = React.useState(false);

  useEffect(() => {
    (async () => {
      let indexResponse: BeaconChainValidatorResponse[] = [];

      const fetchValidators = async (offset: number) => {
        // Wait a bit so we don't hit a rate limiter
        await new Promise(f => setTimeout(f, 200));

        await fetch(
          `${BEACONCHAIN_URL}/api/v1/validator/withdrawalCredentials/${account}?limit=${indexesPaginationSize}&offset=${offset}`
        )
          .then(r => r.json())
          .then(
            async ({
              data,
            }: {
              data:
                | BeaconChainValidatorResponse[]
                | BeaconChainValidatorResponse;
            }) => {
              setShowDepositVerificationWarning(false);
              const response = Array.isArray(data) ? data : [data];
              // no validators for that user's wallet address
              if (response.length === 0 && offset === 0) {
                setValidators([]);
                setLoading(false);

                return;
              }

              indexResponse = indexResponse.concat(response);

              if (response.length === indexesPaginationSize) {
                await fetchValidators(offset + indexesPaginationSize);

                return;
              } else {
                for (
                  let i = 0;
                  i < indexResponse.length;
                  i += validatorPaginationSize
                ) {
                  const indexes = `${indexResponse
                    .slice(
                      i,
                      Math.min(
                        i + validatorPaginationSize,
                        indexResponse.length
                      )
                    )
                    .map(validator => validator.validatorindex)
                    .join(',')}`;

                  // Wait a bit so we don't hit a rate limiter
                  // eslint-disable-next-line
                  await new Promise(f => setTimeout(f, 200));
                  fetch(`${BEACONCHAIN_URL}/api/v1/validator/${indexes}`)
                    .then(r => r.json())
                    .then(({ data }: { data: BeaconChainValidator[] }) => {
                      if (data.length === 0 && i === 0) {
                        setShowDepositVerificationWarning(true);
                      }
                      setValidators(validators => [...validators, ...data]);
                      setLoading(false);
                    })
                    .catch(error => {
                      console.log(error);
                      setLoading(false);
                      setValidatorLoadError(true);
                    });
                }
              }
            }
          )
          .catch(error => {
            console.log(error);
            setLoading(false);
            setValidatorLoadError(true);
          });

        setLoading(false);
      };

      setLoading(true);
      const network = NetworkChainId[chainId as number];

      const isValidNetwork = Object.values(AllowedNetworks).includes(network);

      if (active && account && isValidNetwork) {
        await fetchValidators(0);
      }
    })();
  }, [account, chainId, active]);

  return {
    validators,
    loading,
    setLoading,
    validatorLoadError,
    showDepositVerificationWarning,
  };
};

export default useValidators;
